import { IonIcon } from '@ionic/react';
import { addCircleOutline, shirtOutline, swapHorizontalOutline } from 'ionicons/icons';
import { PlayerAvatar } from '../../../components/PlayerAvatar';
import type { LineupSlot, LineupSlotOccupant } from '../../services/LineupService';
import { cn } from '../../../../lib/utils';
import { FantasyScore } from '../../../components/FantasyScore';

interface LineupPitchSlotProps {
  slot: LineupSlot<LineupSlotOccupant>;
  requiredRole: string;
  onSelect: () => void;
  mode?: 'edit' | 'view';
}

const ROLE_STYLES: Record<string, string> = {
  PORTERO: 'border-amber-200/70',
  DEFENSA: 'border-sky-200/70',
  CENTROCAMPISTA: 'border-emerald-200/70',
  DELANTERO: 'border-rose-200/70',
};

const PLAYER_NAME_CLASS =
  'mt-2 line-clamp-3 max-w-full break-words hyphens-auto text-center text-[12px] font-semibold leading-[13px] text-white sm:line-clamp-2 sm:text-[13px] sm:leading-4';

export const LineupPitchSlot = ({
  slot,
  requiredRole,
  onSelect,
  mode = 'edit',
}: LineupPitchSlotProps) => {
  const player = slot.fantasyTeamPlayer?.player;
  const roleClassName = ROLE_STYLES[requiredRole] ?? 'border-slate-200/70';
  const team = player?.team;
  const isView = mode === 'view';

  return (
    <div
      className="relative flex flex-col items-center"
      data-testid={player ? `lineup-player-${player.id}` : `empty-slot-${slot.position}`}
    >
      {player ? (
        <>
          {isView ? (
            <div
              className="relative flex w-full min-w-0 flex-col items-center rounded-[1.25rem] bg-transparent px-1.5 pb-2 pt-3"
              data-testid={`lineup-slot-${slot.position}`}
            >
              <div className="relative">
                <PlayerAvatar image={player.image} nick={player.nick} />
                {team && (
                  <div
                    className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-slate-100 text-[9px] font-bold uppercase text-slate-700 shadow-sm"
                    data-testid={`team-badge-${player.id}`}
                  >
                    {team.shield ? (
                      <img
                        src={team.shield}
                        alt={team.shortName || team.nameShow || 'Escudo'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{team.shortName || ''}</span>
                    )}
                  </div>
                )}
              </div>
              <span className={PLAYER_NAME_CLASS}>
                {player.nick}
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onSelect}
              className={cn(
                'relative flex w-full min-w-0 flex-col items-center rounded-[1.25rem] border bg-transparent px-1.5 pb-2 pt-3 transition-transform duration-150',
                'hover:-translate-y-0.5',
                'focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)] focus:ring-offset-2',
                roleClassName,
              )}
              data-testid={`lineup-slot-${slot.position}`}
            >
              <div className="relative">
                <PlayerAvatar image={player.image} nick={player.nick} />
                {team && (
                  <div
                    className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-slate-100 text-[9px] font-bold uppercase text-slate-700 shadow-sm"
                    data-testid={`team-badge-${player.id}`}
                  >
                    {team.shield ? (
                      <img
                        src={team.shield}
                        alt={team.shortName || team.nameShow || 'Escudo'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{team.shortName || ''}</span>
                    )}
                  </div>
                )}
              </div>
              <span className={PLAYER_NAME_CLASS}>
                {player.nick}
              </span>
            </button>
          )}

          {isView ? (
            <div className="absolute -right-1.5 -top-1.5" data-testid={`fantasy-score-${slot.position}`}>
              <FantasyScore value={slot.fantasyScore} />
            </div>
          ) : slot.fantasyScore != null ? (
            // In edit mode the score replaces the swap button: the slot itself still opens the selector.
            <div className="absolute -right-1.5 -top-1.5" data-testid={`fantasy-score-${slot.position}`}>
              <FantasyScore value={slot.fantasyScore} />
            </div>
          ) : (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onSelect();
              }}
              className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-[var(--ion-color-primary)] p-0 text-white shadow-md transition-transform duration-150 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--ion-color-primary)] focus:ring-offset-2"
              aria-label={`Sustituir a ${player.nick}`}
            >
              <IonIcon icon={swapHorizontalOutline} className="text-[12px]" />
            </button>
          )}
        </>
      ) : (
        isView ? (
          <div
            className={cn(
              'flex w-full min-w-0 flex-col items-center rounded-[1.25rem] border border-dashed px-2 pb-3 pt-4 shadow-sm',
              'bg-white/15 backdrop-blur-sm',
            )}
            data-testid={`lineup-slot-view-${slot.position}`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/40 bg-white/15 text-white shadow-inner">
              <IonIcon icon={shirtOutline} className="text-2xl" />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSelect}
            className={cn(
              'flex w-full min-w-0 flex-col items-center rounded-[1.25rem] border border-dashed px-2 pb-3 pt-4 shadow-sm transition-transform duration-150',
              'bg-white/15 backdrop-blur-sm hover:-translate-y-0.5 hover:bg-white/20',
              'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-700',
            )}
            data-testid={`lineup-slot-${slot.position}`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/40 bg-white/15 text-white shadow-inner">
              <IonIcon icon={shirtOutline} className="text-2xl" />
            </div>
            <IonIcon icon={addCircleOutline} className="mt-2 text-2xl text-white/90" />
          </button>
        )
      )}
    </div>
  );
};
